export type DashboardPeriodType = "month" | "quarter" | "year" | "custom";

export interface DashboardPeriodSelection {
    type: DashboardPeriodType;
    month: string;
    quarter: number;
    year: number;
    customStartDate: string;
    customEndDate: string;
}

export interface DashboardDateRange {
    startDate: string;
    endDate: string;
}

const pad = (value: number) => String(value).padStart(2, "0");

const formatDate = (year: number, month: number, day: number) =>
    `${year}-${pad(month)}-${pad(day)}`;

const isLeapYear = (year: number) => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const getDaysInMonth = (year: number, month: number) => {
    if (month === 2) return isLeapYear(year) ? 29 : 28;
    return [4, 6, 9, 11].includes(month) ? 30 : 31;
};

const isValidIsoDate = (value: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return false;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    return year >= 1 && month >= 1 && month <= 12 && day >= 1 && day <= getDaysInMonth(year, month);
};

export const createDefaultDashboardPeriod = (now = new Date()): DashboardPeriodSelection => {
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const startDate = formatDate(year, month, 1);
    const endDate = formatDate(year, month, getDaysInMonth(year, month));

    return {
        type: "month",
        month: `${year}-${pad(month)}`,
        quarter: Math.floor((month - 1) / 3) + 1,
        year,
        customStartDate: startDate,
        customEndDate: endDate,
    };
};

export const getDashboardDateRange = (
    selection: DashboardPeriodSelection,
): DashboardDateRange | undefined => {
    if (selection.type === "month") {
        const match = /^(\d{4})-(\d{2})$/.exec(selection.month);
        if (!match) return undefined;

        const year = Number(match[1]);
        const month = Number(match[2]);
        if (month < 1 || month > 12) return undefined;

        return {
            startDate: formatDate(year, month, 1),
            endDate: formatDate(year, month, getDaysInMonth(year, month)),
        };
    }

    if (!Number.isInteger(selection.year) || selection.year < 1 || selection.year > 9999) {
        return undefined;
    }

    if (selection.type === "quarter") {
        if (!Number.isInteger(selection.quarter) || selection.quarter < 1 || selection.quarter > 4) {
            return undefined;
        }

        const startMonth = (selection.quarter - 1) * 3 + 1;
        const endMonth = startMonth + 2;
        return {
            startDate: formatDate(selection.year, startMonth, 1),
            endDate: formatDate(
                selection.year,
                endMonth,
                getDaysInMonth(selection.year, endMonth),
            ),
        };
    }

    if (selection.type === "year") {
        return {
            startDate: formatDate(selection.year, 1, 1),
            endDate: formatDate(selection.year, 12, 31),
        };
    }

    if (
        selection.type !== "custom" ||
        !isValidIsoDate(selection.customStartDate) ||
        !isValidIsoDate(selection.customEndDate) ||
        selection.customStartDate > selection.customEndDate
    ) {
        return undefined;
    }

    return {
        startDate: selection.customStartDate,
        endDate: selection.customEndDate,
    };
};

export const restoreDashboardPeriod = (
    storedValue: string | null,
    fallback = createDefaultDashboardPeriod(),
): DashboardPeriodSelection => {
    if (!storedValue) return fallback;

    try {
        const parsed = JSON.parse(storedValue) as Partial<DashboardPeriodSelection>;
        if (!parsed.type || !["month", "quarter", "year", "custom"].includes(parsed.type)) {
            return fallback;
        }
        const restored: DashboardPeriodSelection = {
            ...fallback,
            ...parsed,
        };

        return getDashboardDateRange(restored) ? restored : fallback;
    } catch {
        return fallback;
    }
};
